import { Paper, Typography, Grid, Chip, Box, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useProjectParticipants } from '../../../api/hooks/useProjects';
import { useEmployeesByIds } from '../../../api/hooks/useEmployees';

interface ProjectInfoCardProps {
  project: {
    /** Optional, da einige Testfixtures/Altaufrufe die id (noch) nicht mitgeben —
     *  ohne id wird der Projektleiter-Block einfach übersprungen. */
    id?: string;
    state: string;
    priority: string;
    bill_type: string;
    project_no: string;
    project_reference: string;
    deadline: string | null;
    planned_start: string | null;
    planned_end: string | null;
    description: string;
    project_type_id: string | null;
    link_uri: string | null;
  };
}

/** Teilnehmer-Ausschnitt, wie ihn GET /projects/:id/participants liefert
 *  (gleiche Quelle wie ParticipantSection — hier nur die Projektleitung). */
interface Participant {
  id: string;
  coworker_id: string;
  role: string;
  coworker_name?: string;
}

/**
 * Löst die Projektleitung (Teilnehmer mit role=MANAGER) auf einen Klarnamen
 * auf. Nutzt dieselben Hooks wie ParticipantSection (React-Query dedupliziert
 * den Request über den gemeinsamen Query-Key — kein zusätzlicher Backend-Call).
 * Fällt NIE auf die rohe coworker_id zurück — ohne Auflösung lieber „—" als UUID.
 */
function useProjectLeadName(projectId: string | undefined): { leadName: string | null; isLoading: boolean } {
  const { data, isLoading } = useProjectParticipants(projectId);
  const participants = (data as unknown as { data?: Participant[] } | undefined)?.data ?? [];
  const manager = participants.find((p) => p.role === 'MANAGER') ?? null;
  const { map: resolvedNames, isLoading: employeesLoading } = useEmployeesByIds(
    manager ? [manager.coworker_id] : [],
  );

  if (!manager) return { leadName: null, isLoading };

  if (manager.coworker_name?.trim()) {
    return { leadName: manager.coworker_name.trim(), isLoading };
  }
  const emp = resolvedNames.get(manager.coworker_id);
  if (emp) {
    const composed = `${emp.first_name} ${emp.last_name}`.trim();
    return { leadName: composed || emp.display_name || null, isLoading: isLoading || employeesLoading };
  }
  return { leadName: null, isLoading: isLoading || employeesLoading };
}

const STATE_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  PLANNED: 'default',
  STARTED: 'info',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELED: 'error',
};

const PRIORITY_COLORS: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  LOW: 'default',
  NORMAL: 'info',
  HIGH: 'warning',
  URGENT: 'error',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE');
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

export function ProjectInfoCard({ project }: ProjectInfoCardProps) {
  const { t } = useTranslation('projects');
  const { leadName, isLoading: leadLoading } = useProjectLeadName(project.id);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>{t('tabs.overview', 'Übersicht')}</Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 6, md: 3 }}>
          <InfoField label={t('project.lead', 'Projektleiter')}>
            <Typography variant="body2">
              {leadLoading ? t('project.leadLoading', 'Lädt…') : leadName ?? '—'}
            </Typography>
          </InfoField>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <InfoField label={t('fields.state', 'Status')}>
            <Chip
              label={t(`states.${project.state}`, project.state)}
              color={STATE_COLORS[project.state] ?? 'default'}
              size="small"
            />
          </InfoField>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <InfoField label={t('fields.priority', 'Priorität')}>
            <Chip
              label={t(`priorities.${project.priority}`, project.priority)}
              color={PRIORITY_COLORS[project.priority] ?? 'default'}
              size="small"
              variant="outlined"
            />
          </InfoField>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <InfoField label={t('fields.billType', 'Abrechnungsart')}>
            <Typography variant="body2">{t(`billTypes.${project.bill_type}`, project.bill_type)}</Typography>
          </InfoField>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <InfoField label={t('fields.projectNo', 'Projekt-Nr.')}>
            <Typography variant="body2">{project.project_no || '—'}</Typography>
          </InfoField>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <InfoField label={t('fields.projectReference', 'Referenz')}>
            <Typography variant="body2">{project.project_reference || '—'}</Typography>
          </InfoField>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <InfoField label={t('fields.deadline', 'Deadline')}>
            <Typography variant="body2">{formatDate(project.deadline)}</Typography>
          </InfoField>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <InfoField label={t('fields.plannedStart', 'Geplanter Start')}>
            <Typography variant="body2">{formatDate(project.planned_start)}</Typography>
          </InfoField>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <InfoField label={t('fields.plannedEnd', 'Geplantes Ende')}>
            <Typography variant="body2">{formatDate(project.planned_end)}</Typography>
          </InfoField>
        </Grid>
        {project.link_uri && (
          <Grid size={{ xs: 12, md: 6 }}>
            <InfoField label={t('fields.linkUri', 'Link')}>
              <Link href={project.link_uri} target="_blank" rel="noopener">
                {project.link_uri}
              </Link>
            </InfoField>
          </Grid>
        )}
        {project.description && (
          <Grid size={{ xs: 12 }}>
            <InfoField label={t('fields.description', 'Beschreibung')}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{project.description}</Typography>
            </InfoField>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}
